import React, { createContext, useState, useEffect, ReactNode } from 'react'

import { Auth0Provider } from '@auth0/auth0-react'
import axios from 'axios'

import { Alert, Loader } from '@/components/ui'
import { isGeotabMode } from '@/services/geotabSession'

import { AuthProvider } from './AuthProvider'
import { GeotabAuthProvider } from './GeotabAuthProvider'

interface ServerConfig {
  AUTH0_DOMAIN: string
  AUTH0_CLIENT_ID: string
  AUTH0_AUDIENCE: string
  AUTH0_SCOPE?: string
  BASE_URL?: string
}

export interface ConfigContextType {
  isLoading: boolean
  config?: {
    auth0domain: string
    auth0clientId: string
    auth0audience: string
    auth0scope: string
    baseUrl: string
  }
}

export const ConfigContext = createContext<ConfigContextType | undefined>(undefined)

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<ServerConfig | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const serverUrl = import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3009'
    axios
      .get<ServerConfig>(`${serverUrl}/app/config`)
      .then((res) => {
        // If Auth0 domain changed, clear stale Auth0 cache
        const prevDomain = localStorage.getItem('auth0_domain')
        if (prevDomain && prevDomain !== res.data.AUTH0_DOMAIN) {
          console.log('Auth0 domain changed from %s to %s, clearing cached tokens', prevDomain, res.data.AUTH0_DOMAIN)
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('@@auth0spajs@@') || key === 'MEGA_API_TOKEN') {
              localStorage.removeItem(key)
            }
          })
        }
        localStorage.setItem('auth0_domain', res.data.AUTH0_DOMAIN)
        window.VITE_MEGALODON_SERVER_URL = res.data.BASE_URL || ''
        setConfig(res.data)
      })
      .catch((err) => {
        console.error('Failed to fetch config from server:', err.message)
        setError('Failed to load configuration from server.')
      })
  }, [])

  if (error) {
    return <Alert message={error} type="error" />
  }

  if (!config) {
    return <Loader fullScreen />
  }

  const configValue: ConfigContextType = {
    isLoading: false,
    config: {
      auth0domain: config.AUTH0_DOMAIN,
      auth0clientId: config.AUTH0_CLIENT_ID,
      auth0audience: config.AUTH0_AUDIENCE,
      auth0scope: config.AUTH0_SCOPE || 'openid profile email',
      baseUrl: config.BASE_URL || '',
    },
  }

  // Running as a MyGeotab add-in: MyGeotab is the identity provider, so Auth0Provider is
  // deliberately never mounted. That is what avoids the redirect to Auth0's login page,
  // which sets `frame-ancestors 'none'` and cannot be displayed inside the add-in iframe.
  if (isGeotabMode()) {
    return (
      <ConfigContext.Provider value={configValue}>
        <GeotabAuthProvider>{children}</GeotabAuthProvider>
      </ConfigContext.Provider>
    )
  }

  return (
    <ConfigContext.Provider value={configValue}>
      <Auth0Provider
        domain={config.AUTH0_DOMAIN}
        clientId={config.AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: config.AUTH0_AUDIENCE,
          scope: config.AUTH0_SCOPE || 'openid profile email',
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
        skipRedirectCallback={false}
        onRedirectCallback={(appState) => {
          window.history.replaceState({}, document.title, appState?.returnTo || '/')
          window.dispatchEvent(new PopStateEvent('popstate'))
        }}
      >
        <AuthProvider>{children}</AuthProvider>
      </Auth0Provider>
    </ConfigContext.Provider>
  )
}
